import { useState, useEffect, useRef } from 'react';
import '../styles/floating-tabs-panel.scss';
import CloseIcon from '@mui/icons-material/Close';
import { Badge, Button, IconButton, styled } from '@mui/material';
import { isMobile } from 'react-device-detect';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -10,
    top: 15,
    border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
    padding: '0 4px',
  },
}));

export const FloatingTabsPanel = ({
    isOpen,
    onClose,
    anchorEl,
    activeTab: externalActiveTab,
    onTabChange,
    tabs = [],
    className = '',
    collapsed = false
}) => {
    const [internalActiveTab, setInternalActiveTab] = useState(0);
    const [position, setPosition] = useState({ top: 90,  left: 208 });
    const panelRef = useRef(null);

    const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
    const setActiveTab = onTabChange || setInternalActiveTab;

    const ActiveComponent = tabs[activeTab]?.component;

    useEffect(() => {
        if (!isMobile && anchorEl && isOpen) {
            const rect = anchorEl.getBoundingClientRect();
            setPosition({
                top: window.innerHeight - rect.top + 8,
                left: window.innerWidth - rect.left - 4
            });
        }
    }, [isMobile, anchorEl, isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isMobile &&
                panelRef.current &&
                !panelRef.current.contains(event.target)) 
            {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, anchorEl, onClose, isOpen]);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isMobile && isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobile, isOpen]);

    if (!isOpen) return null;

    return (
        <>
            {isMobile && (
                <div className="floating-tabs__overlay" onClick={onClose} />
            )}

            <div
                ref={panelRef}
                className={`floating-tabs ${isMobile ? 'floating-tabs--mobile' : 'floating-tabs--desktop'} ${className}`}
                style={!isMobile ? {
                    position: 'fixed',
                    top: position.top,
                    left: !collapsed ? position.left : position.left - 128,
                    zIndex: 1000
                } : {}}
            >
                {!isMobile && <div className="floating-tabs__arrow" />}

                <div className="floating-tabs__header">
                    <div className="floating-tabs__tabs" style={isMobile ? {width: "calc(100% - 40px)"} : {}}>
                        {tabs.map((tab, index) => (

                            <Button
                                key={tab.id}
                                className={`floating-tabs__tab ${activeTab === index ? 'floating-tabs__tab--active' : ''}`}
                                onClick={() => setActiveTab(index)}
                            >
                                <StyledBadge className="floating-tabs__badge" badgeContent={tab.unreadCount} color="error">
                                    <span className="floating-tabs__tab-icon">{tab.icon}</span>
                                    <span className="floating-tabs__tab-label">{tab.label}</span>
                                </StyledBadge>
                            </Button>
                        ))}
                    </div>
                    {isMobile && (
                        <IconButton onClick={onClose} className="floating-tabs__close">
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>

                <div className="floating-tabs__content">
                    <ActiveComponent />
                </div>
            </div>
        </>
    );
};